import Vue from 'vue';
import Router from 'vue-router';

Vue.use(Router);

// Auth
import Store from '../store';
import { isAuthenticated } from '../mixins/auth';
import { getAuthToken } from '../helpers/localStorage';
import { ACCESS_ROUTE_DECISION, decideAccessRoute } from '../helpers/accessRoute';
import {
  APPOINTMENT_PLANNING_ACCESS_NODE,
  APPOINTMENT_SLOTS_ACCESS_NODE,
  INVOICES_ACCESS_NODE,
  OFFERS_ACCESS_NODE,
  ITEM_INVENTORIES_ACCESS_NODE,
  ITEM_TRANSACTIONS_ACCESS_NODE,
  ITEM_PACKAGES_ACCESS_NODE,
  ITEM_STORAGES_ACCESS_NODE,
  ITEMS_ACCESS_NODE,
  ORDERS_ACCESS_NODE,
  SUPPLIERS_ACCESS_NODE,
  TASKS_ACCESS_NODE,
  TIME_TRACKING_ACCESS_NODE,
} from '../navigation/accessNodes';
import {
  hasRole,
  authorize,
  authorizeByUser,
  hasResourcePermission,
} from '../helpers/userCapabilities';

// Layouts
import Layout from '@/components/Layout/Layout';
import MobileLayout from '@/components/Layout/MobileLayout';
import Print from '@/components/Layout/Print';

// Views
import Login from '../views/Login/CustomLogin.vue';

import { isMobile } from '@/helpers/browser.js';

// Import enums for resource-based access control
import { ResourceType, AuthorizableActions } from '../helpers/enums.js';

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },
  {
    path: '/',
    name: 'home',
    beforeEnter: (to, from, next) => {
      const user = Store.getters['auth/getCurrentUser'];

      if (
        (user.authorizationAttributes?.role === 'ROLE_STANDARD' &&
          user.authorizationAttributes?.department === 'SALES') ||
        (user.authorizationAttributes?.role === 'TEAM_LEAD' &&
          user.authorizationAttributes?.department === 'SALES')
      ) {
        next({ name: 'AdminSalesPage' });
      } else if (user.authorizationAttributes?.role === 'AUDITOR') {
        next({ name: 'AppointmentsPage' });
      } else if (isMobile()) {
        next({ name: 'MobileProjectsPage' });
      } else {
        next({ name: 'ProjectsPaginatedPage' });
      }
    },
  },
  {
    path: '*',
    component: () => import('@/views/Error/404'),
  },
  {
    path: '/403',
    name: '403',
    component: () => import('@/views/Error/403'),
  },
  {
    path: '/',
    component: Print,
    meta: {
      requiresAuthentication: true,
    },
    children: [
      {
        path: '/projekte/:projectNumber/drucken',
        name: 'ProjectPrintPage',
        component: () => import('@/views/Projects/ProjectPrint'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Project :projectNumber Print ',
        },
      },
      {
        path: '/projekte/:projectNumber/drucken/:documentNumber',
        name: 'ProjectDocumentPrintPage',
        component: () => import('@/views/Projects/ProjectDocumentPrint'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Project :projectNumber Document Print',
        },
      },
      {
        path: '/projekte/:projectNumber/berichte/drucken',
        name: 'ReportPrintPage',
        component: () => import('@/views/Report/ReportPrint'),
        props: true,
        meta: {
          requiredRole: 'EDITOR',
          title: 'Report Print ',
        },
      },
      {
        path: '/projekte/:projectNumber/aufmass/drucken',
        name: 'MeasurementPrintPage',
        component: () => import('@/views/Measurement/MeasurementPrint'),
        props: true,
        meta: {
          requiredRole: 'EDITOR',
          title: 'Measurment Print ',
        },
      },
      {
        path: '/projekte/:projectNumber/shortteaser/drucken',
        name: 'ShortTeaserPrintPage',
        component: () => import('@/views/ShortTeaser/ShortTeaserPrint'),
        props: true,
        meta: {
          requiredRole: 'EDITOR',
          title: 'Short Teaser Print',
        },
      },
      {
        path: '/projekte/:projectNumber/masterpresentation/drucken',
        name: 'MasterPresentationPrintPage',
        component: () => import('@/views/MasterPresentation/MasterPresentationPrint'),
        props: true,
        meta: {
          requiredRole: 'EDITOR',
          title: 'Master Presentation',
        },
      },
      {
        path: '/projekte/:projectNumber/salesreport/drucken',
        name: 'SalesReportPrintPage',
        component: () => import('@/views/SalesReport/SalesReportPrint'),
        props: true,
        meta: {
          requiredRole: 'EDITOR',
          title: 'Sales Report Print',
        },
      },
      {
        path: '/projekte/:projectNumber/service/drucken',
        name: 'ServicePrintPage',
        component: () => import('@/views/Service/ServicePrint'),
        props: true,
        meta: {
          requiredRole: 'EDITOR',
          title: 'Service Print ',
        },
      },
      {
        path: '/projekte/:projectNumber/gutachten/drucken',
        name: 'AssessmentPrintPage',
        component: () => import('@/views/Assessment/AssessmentPrint'),
        props: true,
        meta: {
          requiredRole: 'EDITOR',
          title: 'Assessment Print ',
        },
      },
      {
        path: '/projekte/:projectNumber/angebot/drucken',
        name: 'ProjectOfferPrintPage',
        component: () => import('@/views/Projects/ProjectQcellsOfferPrint'),
        props: true,
        meta: {
          requiredRole: 'EDITOR',
          title: 'Project :projectNumber Offer Print ',
        },
      },
      {
        path: '/projekte/:projectNumber/materialliste/drucken',
        name: 'ConsumablesListPrintPage',
        component: () => import('@/views/Projects/ConsumablesListPrint'),
        props: true,
        meta: {
          requiredRole: 'EDITOR',
          title: 'Consumables List ',
        },
      },
    ],
  },
  {
    path: '/mobile',
    component: MobileLayout,
    meta: {
      requiresAuthentication: true,
    },
    children: [
      /**
       * Mobile Project search
       */
      {
        path: 'projekte',
        name: 'MobileProjectsPage',
        component: () => import('@/views/Projects/MobileProjects'),
        meta: {
          authorizedRoles: ['INSTALLATION', 'TEAM_DC', 'TEAM_AC', 'ADMIN', 'CLIENT'],
          title: 'Mobile Projects ',
        },
      },
    ],
  },
  {
    path: '/app',
    component: Layout,
    meta: {
      requiresAuthentication: true,
    },
    children: [
      /**
       * Projekte
       */
      {
        path: '/projekte',
        name: 'ProjectsPaginatedPage',
        component: () => import('@/views/Projects/ProjectsPaginated'),
        meta: {
          authorizedRoles: ['ADMIN', 'CLIENT', 'GUEST'],
          resourceType: ResourceType.PROJECT,
          requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
          title: 'Projekte',
        },
      },
      /**
       * GPS Data
       */
      {
        path: '/gps',
        name: 'GpsPage',
        component: () => import('@/views/Gps/Gps'),
        meta: {
          resourceType: ResourceType.GPS,
          requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
          title: 'GPS Daten',
        },
      },
      {
        path: '/projekte/:projectNumber/bearbeiten',
        name: 'ProjectEditPage',
        component: () => import('@/views/Projects/ProjectEdit'),
        props: (route) => ({
          projectNumber: route.params.projectNumber,
          projectTab: route.query.projectTab,
        }),
        meta: {
          authorizedRoles: ['ADMIN', 'CLIENT', 'GUEST'],
          title: 'Projekt :projectNumber bearbeiten',
          resourceType: ResourceType.PROJECT,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/projekte/neu',
        name: 'ProjectCreatePage',
        component: () => import('@/views/Projects/ProjectEdit'),
        meta: {
          authorizedRoles: ['ADMIN', 'CLIENT'],
          title: 'Projekt erstellen',
          resourceType: ResourceType.PROJECT,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },

      /**
       * Lager
       */
      {
        path: '/material/lager',
        name: 'ItemStoragesPage',
        component: () => import('@/views/Items/ItemStorages'),
        meta: {
          accessNodeId: ITEM_STORAGES_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          requiredRole: 'ADMIN',
          title: 'Lager',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/material/lager/:itemStorageNumber/bearbeiten',
        name: 'ItemStorageEditPage',
        component: () => import('@/views/Items/ItemStorageEdit'),
        props: true,
        meta: {
          accessNodeId: ITEM_STORAGES_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          requiredRole: 'ADMIN',
          title: 'Lager :itemStorageNumber bearbeiten',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/material/lager/neu',
        name: 'ItemStorageCreatePage',
        component: () => import('@/views/Items/ItemStorageEdit'),
        meta: {
          accessNodeId: ITEM_STORAGES_ACCESS_NODE,
          requiredAccessLevel: 'editor',
          requiredRole: 'ADMIN',
          title: 'Lager erstellen',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Material Stammdaten
       */
      {
        path: '/material/stammdaten',
        name: 'ItemsPage',
        component: () => import('@/views/Items/Items'),
        meta: {
          accessNodeId: ITEMS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          requiredRole: 'ADMIN',
          title: 'Material',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/material/stammdaten/:itemNumber/bearbeiten',
        name: 'ItemEditPage',
        component: () => import('@/views/Items/ItemEdit'),
        props: true,
        meta: {
          accessNodeId: ITEMS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          requiredRole: 'ADMIN',
          title: 'Material :itemNumber bearbeiten',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/material/stammdaten/neu',
        name: 'ItemCreatePage',
        component: () => import('@/views/Items/ItemEdit'),
        meta: {
          accessNodeId: ITEMS_ACCESS_NODE,
          requiredAccessLevel: 'editor',
          requiredRole: 'ADMIN',
          title: 'Material erstellen',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Inventar
       */
      {
        path: '/material/inventar',
        name: 'ItemInventoriesPage',
        component: () => import('@/views/Items/ItemInventories'),
        meta: {
          accessNodeId: ITEM_INVENTORIES_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          title: 'Inventar',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      /**
       * Material Transaktionen
       */
      {
        path: '/material/transaktionen',
        name: 'ItemTransactionsPage',
        component: () => import('@/views/Items/ItemTransactions'),
        meta: {
          accessNodeId: ITEM_TRANSACTIONS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          title: 'Material Transaktionen',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      /**
       * Bestellungen
       */
      {
        path: '/material/bestellungen',
        name: 'OrdersPage',
        component: () => import('@/views/Orders/Orders'),
        meta: {
          accessNodeId: ORDERS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          title: 'Bestellungen',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/material/bestellungen/:orderNumber/bearbeiten',
        name: 'OrderEditPage',
        component: () => import('@/views/Orders/OrderEdit'),
        props: true,
        meta: {
          accessNodeId: ORDERS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          title: 'Bestellung :orderNumber bearbeiten',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/material/bestellungen/neu',
        name: 'OrderCreatePage',
        component: () => import('@/views/Orders/OrderEdit'),
        meta: {
          accessNodeId: ORDERS_ACCESS_NODE,
          requiredAccessLevel: 'editor',
          title: 'Bestellung erstellen',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },

      /**
       * Pakete
       */
      {
        path: '/material/pakete',
        name: 'ItemPackagesPage',
        component: () => import('@/views/ItemPackages/ItemPackages'),
        meta: {
          accessNodeId: ITEM_PACKAGES_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          title: 'Pakete',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/material/pakete/:itemPackageNumber/bearbeiten',
        name: 'ItemPackageEditPage',
        component: () => import('@/views/ItemPackages/ItemPackageEdit'),
        props: true,
        meta: {
          accessNodeId: ITEM_PACKAGES_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          title: 'Paket :itemPackageNumber bearbeiten',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/material/pakete/neu',
        name: 'ItemPackageCreatePage',
        component: () => import('@/views/ItemPackages/ItemPackageEdit'),
        meta: {
          accessNodeId: ITEM_PACKAGES_ACCESS_NODE,
          requiredAccessLevel: 'editor',
          title: 'Paket erstellen',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },

      /**
       * Lieferanten
       */
      {
        path: '/material/lieferanten',
        name: 'SuppliersPage',
        component: () => import('@/views/Suppliers/Suppliers'),
        meta: {
          accessNodeId: SUPPLIERS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          title: 'Lieferanten',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/material/lieferanten/:supplierNumber/bearbeiten',
        name: 'SupplierEditPage',
        component: () => import('@/views/Suppliers/SupplierEdit'),
        props: true,
        meta: {
          accessNodeId: SUPPLIERS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          title: 'Lieferant :supplierNumber bearbeiten',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/material/lieferanten/neu',
        name: 'SupplierCreatePage',
        component: () => import('@/views/Suppliers/SupplierEdit'),
        meta: {
          accessNodeId: SUPPLIERS_ACCESS_NODE,
          requiredAccessLevel: 'editor',
          title: 'Lieferant erstellen',
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },

      /**
       * Buchhaltung (Accounting)
       */
      {
        path: '/buchhaltung/rechnungen',
        name: 'InvoicesPage',
        component: () => import('@/views/Invoices/Invoices'),
        meta: {
          accessNodeId: INVOICES_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          title: 'Rechnungen',
          resourceType: ResourceType.ACCOUNTING,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/buchhaltung/rechnungen/:invoiceNumber/bearbeiten',
        name: 'InvoiceEditPage',
        component: () => import('@/views/Invoices/InvoiceEdit'),
        props: true,
        meta: {
          accessNodeId: INVOICES_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          title: 'Rechnung :invoiceNumber bearbeiten',
          resourceType: ResourceType.ACCOUNTING,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/buchhaltung/rechnungen/neu',
        name: 'InvoiceCreatePage',
        component: () => import('@/views/Invoices/InvoiceEdit'),
        meta: {
          accessNodeId: INVOICES_ACCESS_NODE,
          requiredAccessLevel: 'editor',
          title: 'Rechnung erstellen',
          resourceType: ResourceType.ACCOUNTING,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },

      /**
       * Matrix-only unit (ADR 0004 addendum "Matrix-only units"): no legacy meta, Legacy-Authz
       * gets the 403 page (see feature page angebote).
       */
      {
        path: '/buchhaltung/angebote',
        name: 'OffersPage',
        component: () => import('@/views/Offers/Offers'),
        meta: {
          accessNodeId: OFFERS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          isMatrixOnly: true,
          title: 'Angebote',
        },
      },
      {
        path: '/buchhaltung/angebote/:offerNumber/bearbeiten',
        name: 'OfferEditPage',
        component: () => import('@/views/Offers/OfferEdit'),
        props: true,
        meta: {
          accessNodeId: OFFERS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          isMatrixOnly: true,
          title: 'Angebot :offerNumber bearbeiten',
        },
      },
      {
        path: '/buchhaltung/angebote/neu',
        name: 'OfferCreatePage',
        component: () => import('@/views/Offers/OfferEdit'),
        meta: {
          accessNodeId: OFFERS_ACCESS_NODE,
          requiredAccessLevel: 'editor',
          isMatrixOnly: true,
          title: 'Angebot erstellen',
        },
      },

      /**
       * Geschäftspartner (Parties)
       */
      {
        path: '/geschaeftspartner',
        name: 'PartiesPage',
        component: () => import('@/views/Parties/Parties'),
        meta: {
          title: 'Geschäftspartner',
          resourceType: ResourceType.PARTY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/geschaeftspartner/neu',
        name: 'PartyCreatePage',
        component: () => import('@/views/Parties/PartyEdit'),
        meta: {
          title: 'Geschäftspartner erstellen',
          resourceType: ResourceType.PARTY,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      {
        path: '/geschaeftspartner/:partyNumber/bearbeiten',
        name: 'PartyEditPage',
        component: () => import('@/views/Parties/PartyEdit'),
        props: true,
        meta: {
          title: 'Geschäftspartner bearbeiten',
          resourceType: ResourceType.PARTY,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },

      /**
       * Berichte
       */
      {
        path: '/berichte/beispielbilder',
        name: 'ReportTemplateImagesPage',
        component: () => import('@/views/Report/ReportTemplateImages'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Report Template Images',
          resourceType: ResourceType.REPORT_TEMPLATE_IMAGE,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/berichte/beispielbilder/:reportTemplateImageNumber/bearbeiten',
        name: 'ReportTemplateImageEditPage',
        component: () => import('@/views/Report/ReportTemplateImageEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Report Template Image Edit',
          resourceType: ResourceType.REPORT_TEMPLATE_IMAGE,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      /**
       * Batterien
       */
      {
        path: '/batterien',
        name: 'BatteriesPage',
        component: () => import('@/views/Batteries/Batteries'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Batterien',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/batterien/:batteryNumber/bearbeiten',
        name: 'BatteryEditPage',
        component: () => import('@/views/Batteries/BatteryEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Batterien :batteryNumber bearbeiten',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/batterien/neu',
        name: 'BatteryCreatePage',
        component: () => import('@/views/Batteries/BatteryEdit'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Batterien erstellen',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Wechselrichter
       */
      {
        path: '/wechselrichter',
        name: 'InvertersPage',
        component: () => import('@/views/Inverters/Inverters'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Wechselrichter',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/wechselrichter/:inverterNumber/bearbeiten',
        name: 'InverterEditPage',
        component: () => import('@/views/Inverters/InverterEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Wechselrichter :inverterNumber bearbeiten',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/wechselrichter/neu',
        name: 'InverterCreatePage',
        component: () => import('@/views/Inverters/InverterEdit'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Wechselrichter :inverterNumber erstellen',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Wallboxen
       */
      {
        path: '/wallboxen',
        name: 'WallboxesPage',
        component: () => import('@/views/Wallboxes/Wallboxes'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Wallboxen',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/wallboxen/:wallboxNumber/bearbeiten',
        name: 'WallboxEditPage',
        component: () => import('@/views/Wallboxes/WallboxEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Wallbox :wallboxNumber bearbeiten',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/wallboxen/neu',
        name: 'WallboxCreatePage',
        component: () => import('@/views/Wallboxes/WallboxEdit'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Wallbox erstellen',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Solar Panels
       */
      {
        path: '/module',
        name: 'SolarPanelsPage',
        component: () => import('@/views/SolarPanels/SolarPanels'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Module',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/module/:solarPanelNumber/bearbeiten',
        name: 'SolarPanelEditPage',
        component: () => import('@/views/SolarPanels/SolarPanelEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Module :solarPanelNumber bearbeiten',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/module/neu',
        name: 'SolarPanelCreatePage',
        component: () => import('@/views/SolarPanels/SolarPanelEdit'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Module erstellen',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Energieflussrichtungssensoren
       */
      {
        path: '/energieflussrichtungssensoren',
        name: 'EnergyFlowDirectionSensorsPage',
        component: () => import('@/views/EnergyFlowDirectionSensors/EnergyFlowDirectionSensors'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Energieflussrichtungssensoren',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/energieflussrichtungssensoren/:energyFlowDirectionSensorNumber/bearbeiten',
        name: 'EnergyFlowDirectionSensorEditPage',
        component: () => import('@/views/EnergyFlowDirectionSensors/EnergyFlowDirectionSensorEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Sensoren :energyFlowDirectionSensorNumber bearbeiten',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/energieflussrichtungssensoren/neu',
        name: 'EnergyFlowDirectionSensorCreatePage',
        component: () => import('@/views/EnergyFlowDirectionSensors/EnergyFlowDirectionSensorEdit'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Energieflussrichtungssensoren erstellen',
          resourceType: ResourceType.SYSTEM,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * EVU's
       */
      {
        path: '/evu',
        name: 'PowerCompaniesPage',
        component: () => import('@/views/PowerCompanies/PowerCompanies'),
        meta: {
          authorizedRoles: ['ADMIN', 'CLIENT'],
          title: 'EVU',
          resourceType: ResourceType.POWER_COMPANY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/evu/:powerCompanyNumber/bearbeiten',
        name: 'PowerCompanyEditPage',
        component: () => import('@/views/PowerCompanies/PowerCompanyEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'EVU :powerCompanyNumber bearbeiten',
          resourceType: ResourceType.POWER_COMPANY,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/evu/neu',
        name: 'PowerCompanyCreatePage',
        component: () => import('@/views/PowerCompanies/PowerCompanyEdit'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'EVU erstellen',
          resourceType: ResourceType.POWER_COMPANY,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Dokumente
       */
      {
        path: '/dokumente',
        name: 'DocumentsPage',
        component: () => import('@/views/Documents/Documents'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Dokumente',
          resourceType: ResourceType.POWER_COMPANY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/dokumente/:documentNumber/bearbeiten',
        name: 'DocumentEditPage',
        component: () => import('@/views/Documents/DocumentEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Dokumente :documentNumber bearbeiten',
          resourceType: ResourceType.POWER_COMPANY,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/dokumente/neu',
        name: 'DocumentCreatePage',
        component: () => import('@/views/Documents/DocumentEdit'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Dokumente erstellen',
          resourceType: ResourceType.POWER_COMPANY,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Angebotspositionen-Vorlagen
       */
      {
        path: '/angebotspositionen-vorlagen',
        name: 'OfferPositionBoilerplatesPage',
        component: () => import('@/views/OfferPositionBoilerplates/OfferPositionBoilerplates'),
        meta: {
          title: 'Angebotspositionen-Vorlagen',
          resourceType: ResourceType.ACCOUNTING,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/angebotspositionen-vorlagen/:offerPositionBoilerplateNumber/bearbeiten',
        name: 'OfferPositionBoilerplateEditPage',
        component: () => import('@/views/OfferPositionBoilerplates/OfferPositionBoilerplateEdit'),
        props: true,
        meta: {
          title: 'Angebotspositionen-Vorlage :offerPositionBoilerplateNumber bearbeiten',
          resourceType: ResourceType.ACCOUNTING,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/angebotspositionen-vorlagen/neu',
        name: 'OfferPositionBoilerplateCreatePage',
        component: () => import('@/views/OfferPositionBoilerplates/OfferPositionBoilerplateEdit'),
        meta: {
          title: 'Angebotspositionen-Vorlage erstellen',
          resourceType: ResourceType.ACCOUNTING,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Auftraggeber
       */
      {
        path: '/auftraggeber',
        name: 'EmployersPage',
        component: () => import('@/views/Employers/Employers'),
        meta: {
          authorizedRoles: ['ADMIN', 'CLIENT'],
          title: 'Auftraggeber',
          resourceType: ResourceType.EMPLOYER,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/auftraggeber/:employerNumber/bearbeiten',
        name: 'EmployerEditPage',
        component: () => import('@/views/Employers/EmployerEdit'),
        props: true,
        meta: {
          authorizedRoles: ['ADMIN', 'CLIENT'],
          title: 'Auftraggeber :employerNumber bearbeiten',
          resourceType: ResourceType.EMPLOYER,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/auftraggeber/neu',
        name: 'EmployerCreatePage',
        component: () => import('@/views/Employers/EmployerEdit'),
        meta: {
          authorizedRoles: ['ADMIN', 'CLIENT'],
          title: 'Auftraggeber erstellen',
          resourceType: ResourceType.EMPLOYER,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Mandant
       */
      {
        path: '/mandanten',
        name: 'ClientsPage',
        component: () => import('@/views/Clients/Clients'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Mandanten',
          resourceType: ResourceType.CLIENT,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/mandanten/:clientNumber/bearbeiten',
        name: 'ClientEditPage',
        component: () => import('@/views/Clients/ClientEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Mandaten :clientNumber bearbeiten',
          resourceType: ResourceType.CLIENT,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/mandanten/neu',
        name: 'ClientCreatePage',
        component: () => import('@/views/Clients/ClientEdit'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Mandaten erstellen',
          resourceType: ResourceType.CLIENT,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Mitarbeiter
       */
      {
        path: '/mitarbeiter',
        name: 'EmployeesPage',
        component: () => import('@/views/Employees/Employees'),
        meta: {
          title: 'Mitarbeiter',
          resourceType: ResourceType.EMPLOYEE,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/mitarbeiter/:employeeNumber/bearbeiten',
        name: 'EmployeeEditPage',
        component: () => import('@/views/Employees/EmployeeEdit'),
        props: true,
        meta: {
          title: 'Mitarbeiter :employeeNumber bearbeiten',
          resourceType: ResourceType.EMPLOYEE,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/mitarbeiter/neu',
        name: 'EmployeeCreatePage',
        component: () => import('@/views/Employees/EmployeeEdit'),
        meta: {
          title: 'Mitarbeiter erstellen',
          resourceType: ResourceType.EMPLOYEE,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      {
        path: '/mitarbeiter/auslastung',
        name: 'EmployeeUtilizationPage',
        component: () => import('@/views/Employees/EmployeeUtilization'),
        meta: {
          title: 'Mitarbeiter Auslastung',
          resourceType: ResourceType.EMPLOYEE,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      /**
       * Elektrofachkraft
       */
      {
        path: '/elektrofachkraft',
        name: 'ElectriciansPage',
        component: () => import('@/views/Electricians/Electricians'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Elektrofachkraft',
          resourceType: ResourceType.ELECTRICIAN,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/elektrofachkraft/:electricianNumber/bearbeiten',
        name: 'ElectricianEditPage',
        component: () => import('@/views/Electricians/ElectricianEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Elektrofachkraft :electricianNumber bearbeiten',
          resourceType: ResourceType.ELECTRICIAN,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/elektrofachkraft/neu',
        name: 'ElectricianCreatePage',
        component: () => import('@/views/Electricians/ElectricianEdit'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Elektrofachkraft erstellen',
          resourceType: ResourceType.ELECTRICIAN,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Messstellenbetreiber
       */
      {
        path: '/messstellenbetreiber',
        name: 'MeasurementPointOperatorsPage',
        component: () => import('@/views/MeasurementPointOperators/MeasurementPointOperators'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Messstellenbetreiber',
          resourceType: ResourceType.MEASUREMENT_POINT_OPERATOR,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/messstellenbetreiber/:measurementPointOperatorNumber/bearbeiten',
        name: 'MeasurementPointOperatorEditPage',
        component: () => import('@/views/MeasurementPointOperators/MeasurementPointOperatorEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Messstellenbetreiber :measurementPointOperatorNumber bearbeiten',
          resourceType: ResourceType.MEASUREMENT_POINT_OPERATOR,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/zugangskontrolle',
        name: 'PolicyEditPage',
        component: () => import('@/views/Policies/PolicyEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          resourceType: ResourceType.POLICY,
          requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
          title: 'Zugangskontrolle',
        },
      },
      {
        path: '/zugangskontrolle/rollen',
        name: 'OrganizationRoleMatrixPage',
        component: () => import('@/views/OrganizationRoles/OrganizationRoleMatrix'),
        meta: {
          requiredRole: 'ADMIN',
          resourceType: ResourceType.POLICY,
          requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
          title: 'Rollen',
        },
      },
      {
        path: '/messstellenbetreiber/neu',
        name: 'MeasurementPointOperatorCreatePage',
        component: () => import('@/views/MeasurementPointOperators/MeasurementPointOperatorEdit'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Messtellenbetreiber erstellen',
          resourceType: ResourceType.MEASUREMENT_POINT_OPERATOR,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Users
       */
      {
        path: '/benutzer',
        name: 'UsersPage',
        component: () => import('@/views/Users/Users'),
        meta: {
          requiredRole: 'ADMIN',
          resourceType: ResourceType.USER,
          requiredActionPermissions: [AuthorizableActions.LIST],
          breadcrumbs: [{ name: 'Home', link: 'home' }, { name: 'Benutzer' }],
          title: 'Benutzer',
        },
      },
      {
        path: '/benutzer/erstellen',
        name: 'UserCreatePage',
        component: () => import('@/views/Users/UserEdit'),
        meta: {
          breadcrumbs: [
            { name: 'Home', link: 'home' },
            { name: 'Benutzer', link: 'UsersPage' },
            { name: 'Benutzer erstellen' },
          ],
          title: 'Benutzer erstellen',
          resourceType: ResourceType.USER,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Employee Request
       */
      {
        path: '/mitarbeiterantrag',
        name: 'EmployeeRequestPage',
        component: () => import('@/views/EmployeeRequest/EmployeeRequests'),
        meta: {
          requiredRole: 'ADMIN',
          resourceType: ResourceType.TIME_TRACKING,
          requiredActionPermissions: [AuthorizableActions.LIST],
          breadcrumbs: [{ name: 'Home', link: 'home' }, { name: 'Mitarbeiterantrag' }],
          title: 'Mitarbeiterantrag',
        },
      },
      {
        path: '/mitarbeiterantrag/erstellen',
        name: 'EmployeeRequestCreatePage',
        component: () => import('@/views/EmployeeRequest/EmployeeRequestEdit'),
        meta: {
          breadcrumbs: [
            { name: 'Home', link: 'home' },
            { name: 'Mitarbeiterantrag', link: 'UsersPage' },
            { name: 'Mitarbeiterantrag erstellen' },
          ],
          title: 'Mitarbeiterantrag erstellen',
          resourceType: ResourceType.TIME_TRACKING,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      {
        path: '/mitarbeiterantrag/:employeeRequestNumber/bearbeiten',
        name: 'EmployeeRequestEditPage',
        component: () => import('@/views/EmployeeRequest/EmployeeRequestEdit'),
        props: true,
        meta: {
          title: 'Mitarbeiterantrag bearbeiten',
          resourceType: ResourceType.TIME_TRACKING,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/benutzer/:userNumber/bearbeiten',
        name: 'UserEditPage',
        component: () => import('@/views/Users/UserEdit'),
        props: true,
        meta: {
          requiredRole: 'ADMIN',
          title: 'Benutzer bearbeiten',
          resourceType: ResourceType.USER,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      /**
       * Changelog
       */
      {
        path: '/updates',
        name: 'ChangelogPage',
        component: () => import(/* webpackChunkName: "about" */ '../views/Changelog.vue'),
        meta: {
          resourceType: ResourceType.UPDATE,
          requiredActionPermissions: [AuthorizableActions.READ],
        },
      },
      /**
       * Nachrichten
       */
      {
        path: '/nachrichten',
        name: 'PushNotificationPage',
        component: () => import(/* webpackChunkName: "about" */ '../views/PushNotification.vue'),
        meta: {
          resourceType: ResourceType.PUSH_NOTIFICATION,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.CREATE],
          isLegacyOnly: true,
        },
      },
      /**
       * Terminplanung
       */
      {
        path: '/planungskalender',
        name: 'AppointmentsPage',
        component: () => import('@/views/Appointments/Appointments'),
        meta: {
          accessNodeId: APPOINTMENT_PLANNING_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          authorizedRoles: ['ADMIN', 'CLIENT'],
          title: 'Planungskalender',
          resourceType: ResourceType.APPOINTMENT,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/kalender',
        name: 'AppointmentsCalendarPage',
        component: () => import('@/views/Appointments/AppointmentsCalendar'),
        meta: {
          requiredRole: 'ADMIN',
          title: 'Kalender',
          resourceType: ResourceType.APPOINTMENT,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/terminkapazitaet',
        name: 'AppointmentCapacitiesPage',
        component: () => import('@/views/Appointments/AppointmentCapacities'),
        meta: {
          accessNodeId: APPOINTMENT_SLOTS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          requiredRole: 'ADMIN',
          title: 'Terminkapazitaet',
          resourceType: ResourceType.APPOINTMENT_CAPACITY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      /**
       * Aufgaben
       */
      {
        path: '/aufgaben',
        name: 'TasksPage',
        component: () => import('@/views/Tasks/Tasks'),
        meta: {
          accessNodeId: TASKS_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          authorizedRoles: ['ADMIN', 'CLIENT'],
          title: 'Aufgaben',
          resourceType: ResourceType.TASK,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      /**
       * Bewerber
       */
      {
        path: '/bewerber',
        name: 'ApplicantsPage',
        component: () => import('@/views/Applicants/Applicants'),
        meta: {
          title: 'Bewerber',
          resourceType: ResourceType.APPLICANT,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      /**
       * Dashboard Termine
       */
      {
        path: '/dashboard',
        name: 'DashboardPage',
        component: () => import('@/views/Dashboards/Dashboard'),
        meta: {
          title: 'Dashboard',
          resourceType: ResourceType.PROJECT,
          requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
        },
      },
      /**
       * AdminTimeTracking Dev
       */
      {
        path: '/zeiterfassung',
        name: 'AdminTimeTrackingPage',
        component: () => import('@/views/TimeTracking/AdminTimeTracking'),
        meta: {
          accessNodeId: TIME_TRACKING_ACCESS_NODE,
          requiredAccessLevel: 'viewer',
          authorizedRoles: ['ADMIN'],
          title: 'Zeiterfassung',
          resourceType: ResourceType.TIME_TRACKING,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },

      /**
       * Academy Dev
       */
      {
        path: '/academy',
        name: 'AcademyPage',
        component: () => import('@/views/Academy/AcademyPage'),
        meta: {
          authorizedRoles: ['ADMIN', 'CLIENT', 'STUDENT'],
          title: 'Academy',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.READ],
        },
      },
      {
        path: '/academy/stundenplan',
        name: 'AcademyCalendar',
        component: () => import('@/views/Academy/AcademyCalendar'),
        meta: {
          title: 'Stundenplan',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/academy/lehrplan',
        name: 'Timetable',
        component: () => import('@/views/Academy/Timetable'),
        meta: {
          title: 'Stundenplantermine',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },

      {
        path: '/academy/lehrmaterial',
        name: 'Contents',
        component: () => import('@/views/Academy/Contents'),
        meta: {
          authorizedRoles: ['ADMIN', 'CLIENT', 'STUDENT'],
          title: 'Lehrinhalte',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },

      {
        path: '/academy/klassen',
        name: 'Sessions',
        component: () => import('@/views/Academy/Sessions'),
        meta: {
          authorizedRoles: ['ADMIN', 'CLIENT', 'STUDENT'],
          title: 'Klassen',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/academy/schueler',
        name: 'Students',
        component: () => import('@/views/Academy/Students'),
        meta: {
          title: 'Schüler',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },

      {
        path: '/academy/schueler/neu',
        name: 'StudentCreatePage',
        component: () => import('@/views/Academy/StudentEdit'),
        props: true,
        meta: {
          title: 'Schüler erstellen',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },

      {
        path: '/academy/schueler/:studentNumber/bearbeiten',
        name: 'StudentEditPage',
        component: () => import('@/views/Academy/StudentEdit'),
        props: true,
        meta: {
          title: 'Schüler :studentNumber bearbeiten',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/academy/trainer',
        name: 'Trainers',
        component: () => import('@/views/Academy/Trainers'),
        meta: {
          title: 'Trainer',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },

      {
        path: '/academy/trainer/neu',
        name: 'TrainerCreatePage',
        component: () => import('@/views/Academy/TrainerEdit'),
        props: true,
        meta: {
          title: 'Trainer erstellen',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },

      {
        path: '/academy/trainer/:trainerNumber/bearbeiten',
        name: 'TrainerEditPage',
        component: () => import('@/views/Academy/TrainerEdit'),
        props: true,
        meta: {
          title: 'Trainer :trainerNumber bearbeiten',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/academy/kurse',
        name: 'Courses',
        component: () => import('@/views/Academy/Courses'),
        meta: {
          title: 'Kurse',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },

      {
        path: '/academy/kurs/neu',
        name: 'CourseCreatePage',
        component: () => import('@/views/Academy/CourseEdit'),
        props: true,
        meta: {
          title: 'Kurs erstellen',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },

      {
        path: '/academy/kurs/:courseNumber/bearbeiten',
        name: 'CourseEditPage',
        component: () => import('@/views/Academy/CourseEdit'),
        props: true,
        meta: {
          title: 'Kurs :courseNumber bearbeiten',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },

      {
        path: '/academy/klassen/neu',
        name: 'SessionCreatePage',
        component: () => import('@/views/Academy/SessionEdit'),

        meta: {
          title: 'Klasse erstellen',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },

      {
        path: '/academy/klassen/:sessionNumber/bearbeiten',
        name: 'SessionEditPage',
        component: () => import('@/views/Academy/SessionEdit'),
        props: true,
        meta: {
          title: 'Klasse :sessionNumber bearbeiten',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/academy/lehrmaterial/neu',
        name: 'ContentCreatePage',
        component: () => import('@/views/Academy/ContentEdit'),
        meta: {
          title: 'Lehrmaterial erstellen',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },

      {
        path: '/academy/lehrmaterial/:contentNumber/bearbeiten',
        name: 'ContentEditPage',
        component: () => import('@/views/Academy/ContentEdit'),
        props: true,
        meta: {
          title: 'Lehrmaterial :contentNumber bearbeiten',
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },

      /**
       * Sales
       */
      {
        path: '/sales/overview',
        name: 'AdminSalesPage',
        component: () => import('@/views/Sales/AdminSales'),
        meta: {
          title: 'Sales',
          resourceType: ResourceType.SALES,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/sales/cockpit',
        name: 'SalesCockpitPage',
        component: () => import('@/views/Sales/SalesCockpit'),
        meta: {
          title: 'Sales Cockpit',
          resourceType: ResourceType.SALES,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/sales/neu',
        name: 'SalesCreatePage',
        component: () => import('@/views/Sales/SalesEdit'),
        props: true,
        meta: {
          title: 'Kunde erstellen',
          resourceType: ResourceType.SALES,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      {
        path: '/sales/:projectNumber/bearbeiten',
        name: 'SalesEditPage',
        component: () => import('@/views/Sales/SalesEdit'),
        props: true,
        meta: {
          title: 'Kunde :projectNumber bearbeiten',
          resourceType: ResourceType.SALES,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },

      /**
       * Sales Teams
       */
      {
        path: '/sales/teams',
        name: 'SalesTeamsPage',
        component: () => import('@/views/SalesTeams/SalesTeams'),
        meta: {
          title: 'Sales Teams',
          resourceType: ResourceType.SALES_HIERARCHY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      {
        path: '/sales/teams/:number/bearbeiten',
        name: 'SalesTeamEditPage',
        component: () => import('@/views/SalesTeams/SalesTeamEdit'),
        props: true,
        meta: {
          title: 'Sales Team :number bearbeiten',
          resourceType: ResourceType.SALES_HIERARCHY,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.UPDATE],
        },
      },
      {
        path: '/sales/teams/erstellen',
        name: 'SalesTeamCreatePage',
        component: () => import('@/views/SalesTeams/SalesTeamEdit'),
        meta: {
          title: 'Sales Team erstellen',
          resourceType: ResourceType.SALES_HIERARCHY,
          requiredActionPermissions: [AuthorizableActions.CREATE],
        },
      },
      /**
       * Aktivitätsprotokoll
       */
      {
        path: '/aktivitaetsprotokoll',
        name: 'AuditsPage',
        component: () => import('@/views/Audit/Audits'),
        meta: {
          authorizedRoles: ['ADMIN'],
          title: 'Aktivitätsprotokoll',
          resourceType: ResourceType.AUDIT,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      /**
       * Logging
       */
      {
        path: '/logging',
        name: 'LoggingPage',
        component: () => import('@/views/Logging/Logging'),
        meta: {
          title: 'Logging',
          resourceType: ResourceType.AUDIT,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
      },
      /**
       * Einstellungen
       */
      {
        path: '/einstellungen',
        name: 'SettingsPage',
        component: () => import('@/views/Settings/Settings'),
        meta: {
          title: 'Einstellungen',
          resourceType: ResourceType.SETTINGS,
          requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.UPDATE],
        },
      },
    ],
  },
];

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  Store.dispatch('fadeFlashMessage');
  // Updating the document title
  let pageTitle = to.meta.title;
  if (pageTitle) {
    if (to.params.projectNumber) {
      pageTitle = pageTitle.replace(':projectNumber', to.params.projectNumber);
    }
    if (to.params.employeeNumber) {
      pageTitle = pageTitle.replace(':employeeNumber', to.params.employeeNumber);
    }
    document.title = pageTitle;
  }

  // Login routes don't need policy checks
  if (to.name === 'Login') {
    return next();
  }

  Store.dispatch('auth/loginFromStorage').then(
    (response) => {
      const user = Store.getters['auth/getCurrentUser'];

      // Check if user is logged in
      if (!user) {
        next('/login');
      }
      // INSTALLATION users are not allowed to proceed
      else if (
        user.authorizationAttributes?.role === 'INSTALLATION' &&
        user.authorizationAttributes?.department === 'INSTALLATION' &&
        to.name !== '403'
      ) {
        next({ name: '403' });
      }
      // Check if the route requires authentication
      else if (
        to.matched.some((record) => record.meta.requiresAuthentication) &&
        !isAuthenticated(getAuthToken())
      ) {
        next('/login');
      } else {
        // Role matrix (switch on): routes with accessNodeId follow the user's level, not ABAC
        const accessDecision = decideAccessRoute(to, {
          isSwitchOn: Store.getters['auth/isOrganizationRoleNavigationEnabled'],
          isMyAccessLoaded: Store.getters.isMyAccessLoaded,
          hasMyAccessError: Store.getters.hasMyAccessError,
          getAccessLevel: Store.getters.getAccessLevel,
        });

        if (accessDecision === ACCESS_ROUTE_DECISION.LOAD) {
          // fetchMyAccess sets the error flag instead of throwing; the retry then denies
          Store.dispatch('fetchMyAccess').then(() => {
            router.push(to.fullPath);
          });
          return;
        }
        if (accessDecision === ACCESS_ROUTE_DECISION.DENY) {
          console.log('Access level check failed');
          next({ name: '403' });
          return;
        }
        if (accessDecision === ACCESS_ROUTE_DECISION.ALLOW) {
          Store.dispatch('auth/refreshUser');
          next();
          return;
        }

        // Make sure user policies are loaded before checking permissions
        const policiesLoaded = Store.getters['policies/getPoliciesLoaded'];

        // If we don't have policies loaded yet and need them for this route, load them first
        const needsPolicies = to.matched.some(
          (record) => record.meta.resourceType && record.meta.requiredActionPermissions
        );

        if (needsPolicies && !policiesLoaded) {
          // Load policies and then retry the navigation
          Store.dispatch('policies/fetchUserPolicies')
            .then(() => {
              // Rerun the navigation with the same destination after policies are loaded
              router.push(to.fullPath);
            })
            .catch((error) => {
              console.error('Error loading user policies:', error);
              next({ name: '403' });
            });
          return;
        }

        // Continue with normal authorization flow since policies are loaded
        // Determine which authorization check to use
        const hasResourcePermissionCheck = to.matched.some(
          (record) => record.meta.resourceType && record.meta.requiredActionPermissions
        );
        const hasRoleCheck = to.matched.some((record) => record.meta.requiredRole);
        const hasAuthorizedRolesCheck = to.matched.some((record) => record.meta.authorizedRoles);

        // Priority 1: Check resource-based permissions if defined
        if (hasResourcePermissionCheck) {
          const resourcePermissionResult = to.matched.every(
            (record) =>
              !record.meta.resourceType ||
              !record.meta.requiredActionPermissions ||
              hasResourcePermission(
                user,
                record.meta.resourceType,
                record.meta.requiredActionPermissions
              )
          );

          if (!resourcePermissionResult) {
            console.log('Policy permission check failed');
            next({ name: '403' });
            return;
          }
        }
        // Priority 2: If no resource permissions or they succeeded, check role-based permissions
        else if (hasRoleCheck) {
          if (!hasRole(user, to.meta.requiredRole)) {
            console.log('Role permission check failed');
            next({ name: '403' });
            return;
          }
        }
        // Priority 3: If no resource or role permissions or they succeeded, check authorized roles
        else if (hasAuthorizedRolesCheck) {
          if (!authorize(user, to.meta.authorizedRoles, to.params.slug)) {
            console.log('Authorize check failed', user);
            next({ name: '403' });
            return;
          }
        }

        // All permission checks have been passed or were not required
        // Always update user information to get new notifications
        Store.dispatch('auth/refreshUser');
        next();
      }
    },
    (error) => {
      // console.log(error)
      next({ name: '403' });
    }
  );
});

export default router;
